import { useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import OptionChoice from "../../organisms/OptionChoice/OptionChoice";
import { useAppDispatch, useAppSelector } from "../../../rtk/_config/hooks";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";

import { setBasket } from "../../../rtk/basket/basket";
import { RootState } from "../../../types/rtk/api-config/types";
import Header from "../../templates/Header/Header";
import Footer from "../../templates/Footer/Footer";
import StyledButton from "../../atoms/StyledButton/StyledButton";
import { AppRoutes } from "../../../routes/AppRoutes";
import SpinnerLoader from "../../atoms/SpinnerLoader/SpinnerLoader";
import { usePostPaymentMutation } from "../../../rtk/payment/payment";
import { PaymentRequest } from "../../../types/rtk/payment/payment";
import { useLazyPostPromoQuery } from "../../../rtk/promo/promo";
import { promoRequest } from "../../../types/rtk/promo/promo";

const Options = () => {
  const { t } = useTranslation();
  const navigateWithParams = useNavigateWithParams();
  const dispatch = useAppDispatch();
  const basket = useAppSelector((state: RootState) => state.basket);
  const data = useAppSelector((state: RootState) => state.offers);
  // apiv5 tracker_id comes from the launch URL (auth slice); legacy falls back to
  // the tracker slice populated by GET /tracker/.
  const authTrackerId = useAppSelector((state: RootState) => state.auth.tracker_id);
  const stateTrackerId = useAppSelector((state: RootState) => state.tracker.tracker_id);
  const trackerId = authTrackerId ?? (stateTrackerId || undefined);
  const [sendPayment, { isLoading: payLoading }] = usePostPaymentMutation();
  const [sendPromoCode] = useLazyPostPromoQuery();

  const handleValidatePromoCode = async (code: string | null) => {
    const code_data: promoRequest = {
      offer_id: basket.offer_id,
      promo: code
    };
    const resp = await sendPromoCode({ body: code_data });
    if (resp.isError) return false;
    dispatch(setBasket({ 
      ...basket, 
      promo: resp.data?.promo || null,
      promocode_ratio: resp.data?.ratio || null
    }));
    return true;
  };
  
  useEffect(() => {
    if (basket.offer_id === null) {
      navigateWithParams(AppRoutes.SUBSCRIPTIONS);
    }
  }, [basket.offer_id, navigateWithParams]);

  useEffect(() => {
    if (data && basket.options === null) {
      dispatch(setBasket({ ...basket, options: data.optionsData.map(option => option.id) }));
    }
  }, [data, basket, dispatch]);

  const handlePaymentRedirect = async () => {
    const payRequest : PaymentRequest = {
      offer_id: basket.offer_id,
      options: basket.options || [],
      giftcard: basket.giftcard,
      promo: basket.promo,
      base_url: `${window.location.origin}`,
      query_params: window.location.search,
      tracker_id: trackerId ?? null,
    };
    try {
      const payResponse = await sendPayment(payRequest).unwrap();
      if (payResponse.checkout_url) {
        window.location.href = payResponse.checkout_url;
        return;
      }
    } catch (error) {
      navigateWithParams(AppRoutes.FAIL);
    }
  };

  const handleBack = () => {
    navigateWithParams(AppRoutes.SUBSCRIPTIONS);
  };


  return (
    <>
      <Header
        headerStep={{ context: "new_subscription", step: 2, totalSteps: 2 }}
        title={t("subscription:HEADER.TITLE.SECOND_PAGE")}
        goBack={handleBack}
      />
      {(data) ? <Box className="Option">
        <OptionChoice datas={data.optionsData} />
      </Box> : <SpinnerLoader />}
      <Footer
        headerStep={{ context: "new_subscription", step: 2, totalSteps: 2 }}
        onValidatePromoCode={handleValidatePromoCode}
      >
        <StyledButton
          children={(payLoading) ? <SpinnerLoader /> : t("subscription:FIRST_PAGE.BUTTON")}
          onClick={handlePaymentRedirect}
          disabled={payLoading || !basket.offer_id}
        />
      </Footer>
    </>
  );
};

export default Options;